import React, { useState } from 'react';
import { 
  Card, 
  Select, 
  Space, 
  Typography,
  Table,
  DatePicker,
  Button,
  Tag,
  Empty,
  Tooltip,
  Progress
} from 'antd';
import { 
  FileTextOutlined, 
  CalendarOutlined,
  DownloadOutlined,
  UserOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * 项目日志页面组件
 * 显示选中项目的关联日志列表
 */
const ProjectLogs = () => {
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 获取项目列表
  const { data: projectsData } = useQuery(
    'projects',
    () => axios.get('/projects', { params: { limit: 100 } }).then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  // 获取日志列表
  const { data: logsData, isLoading: logsLoading } = useQuery(
    ['projectLogs', selectedProjectId, dateRange, currentPage, pageSize],
    () => {
      if (!selectedProjectId) return { logs: [], total: 0 };
      
      const params = {
        projectId: selectedProjectId,
        page: currentPage,
        limit: pageSize
      };
      
      if (dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      
      return axios.get('/daily-logs', { params }).then(res => res.data.data);
    },
    { 
      enabled: !!selectedProjectId,
      refetchInterval: 30000 
    }
  );

  /**
   * 处理项目选择变化
   * @param {number} projectId - 选中的项目ID
   */
  const handleProjectChange = (projectId) => {
    setSelectedProjectId(projectId);
    setCurrentPage(1); // 重置页码
  };

  /**
   * 处理日期范围变化
   * @param {Array} dates - 日期范围数组
   */
  const handleDateRangeChange = (dates) => {
    setDateRange(dates || []);
    setCurrentPage(1); // 重置页码
  };

  /**
   * 导出日志数据
   */
  const handleExport = () => {
    if (!selectedProjectId || !logsData?.logs?.length) {
      return;
    }
    
    // 这里可以实现导出功能
    console.log('导出日志数据:', logsData.logs);
  };

  /**
   * 获取进度变化显示
   * @param {number} before - 之前进度
   * @param {number} after - 之后进度
   * @returns {JSX.Element} 进度变化组件
   */
  const getProgressChange = (before, after) => {
    const change = after - before;
    if (change === 0) {
      return <Text type="secondary">无变化</Text>;
    }
    
    return (
      <Space>
        <Text type="secondary">{before}%</Text>
        <Text type={change > 0 ? 'success' : 'danger'}>
          {change > 0 ? '+' : ''}{change}%
        </Text>
        <Text type="secondary">→ {after}%</Text>
      </Space>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: '日期',
      dataIndex: 'logDate',
      key: 'logDate',
      width: 120,
      render: (date) => (
        <Space>
          <CalendarOutlined />
          {dayjs(date).format('MM-DD')}
        </Space>
      ),
      sorter: (a, b) => dayjs(a.logDate).unix() - dayjs(b.logDate).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: '任务名称',
      dataIndex: ['task', 'title'],
      key: 'taskTitle',
      width: 200,
      render: (title, record) => (
        <div>
          <Text strong>{title}</Text>
          {record.task?.priority && (
            <div style={{ marginTop: 4 }}>
              <Tag 
                size="small" 
                color={record.task.priority === 'high' ? 'red' : 
                       record.task.priority === 'medium' ? 'orange' : 'green'}
              >
                {record.task.priority === 'high' ? '高优先级' : 
                 record.task.priority === 'medium' ? '中优先级' : '低优先级'}
              </Tag>
            </div>
          )}
        </div>
      )
    },
    {
      title: '执行人',
      dataIndex: ['user', 'realName'],
      key: 'userName',
      width: 100,
      render: (name, record) => (
        <Space>
          <UserOutlined />
          <Text>{name}</Text>
        </Space>
      )
    },
    {
      title: '今日完成内容',
      dataIndex: 'todayWork',
      key: 'todayWork',
      width: 300,
      render: (work) => (
        <Tooltip title={work}>
          <Text ellipsis style={{ maxWidth: 280 }}>
            {work}
          </Text>
        </Tooltip>
      )
    },
    {
      title: '工作时长',
      dataIndex: 'workHours',
      key: 'workHours',
      width: 100,
      render: (hours) => (
        <Space>
          <ClockCircleOutlined />
          <Text>{hours}h</Text>
        </Space>
      ),
      sorter: (a, b) => a.workHours - b.workHours
    },
    {
      title: '进度变化',
      key: 'progressChange',
      width: 150,
      render: (_, record) => getProgressChange(record.progressBefore, record.progressAfter)
    },
    {
      title: '问题/备注',
      key: 'issues',
      width: 200,
      render: (_, record) => {
        const issues = record.issues;
        const remarks = record.remarks;
        
        if (!issues && !remarks) {
          return <Text type="secondary">无</Text>;
        }
        
        return (
          <div>
            {issues && (
              <div>
                <Text type="warning" strong>问题：</Text>
                <Text>{issues}</Text>
              </div>
            )}
            {remarks && (
              <div style={{ marginTop: issues ? 4 : 0 }}>
                <Text type="secondary">备注：{remarks}</Text>
              </div>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <FileTextOutlined />
            项目日志
          </Space>
        }
        className="status-card"
      >
        {/* 筛选条件 */}
        <div style={{ marginBottom: '24px' }}>
          <Space wrap size="large">
            <div>
              <Text strong style={{ marginRight: 8 }}>选择项目：</Text>
              <Select
                placeholder="请选择项目"
                style={{ width: 300 }}
                value={selectedProjectId}
                onChange={handleProjectChange}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {projectsData?.projects?.map(project => (
                  <Option key={project.id} value={project.id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            </div>
            
            {selectedProjectId && (
              <>
                <div>
                  <Text strong style={{ marginRight: 8 }}>日期范围：</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    placeholder={['开始日期', '结束日期']}
                  />
                </div>
                
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={!logsData?.logs?.length}
                >
                  导出日志
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* 日志列表 */}
        {!selectedProjectId ? (
          <Empty 
            description="请先选择一个项目查看日志"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={logsData?.logs || []}
            rowKey="id"
            loading={logsLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: logsData?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }
            }}
            scroll={{ x: 1200 }}
            locale={{ emptyText: '暂无日志数据' }}
          />
        )}
      </Card>
    </div>
  );
};

export default ProjectLogs;